import { mapActions, mapGetters, mapState } from 'vuex';
import { cloneDeep, merge } from 'lodash';
import { DATE_PORTION } from '@/services/charts-service/constants';
import { getUsage } from '@/utils/granularity';
import { FILTER_NAMES, HEIGHT, WIDTH, WIDTH_SIZE_LABEL, HEIGHT_SIZE_LABEL, DATA_TYPE, DATA_TYPE_FILTERS_METHODS_OPTIONS } from '@/store/modules/cards/constants';
import { generateRandomId } from '@/utils/math';
import { channels } from '@/utils/channels';
import { trackEventWithUserProperties } from '@/utils/mixpanel';
import { Flags } from '@/plugins/rollout';
import WarRoomService from '@/services/WarRoomService';

export default {
  data() {
    return {
      showFilterListeningModal: false,
      showFilterSacModal: false,
      showFilterPageMetricsModal: false,
      showFilterCompetitiveModal: false,
      showFilterSacStatusModal: false,
      showFilterSacListeningModal: false,
      showDeleteCardModal: false,
      showExpandCardModal: false,
      showExpandIcon: false,
      showResizeModal: false,
      showFilterBlipModal: false,
      showGenericFilterModal: false,
      showTagViewModal: false,
    };
  },
  computed: {
    ...mapGetters('dashboard', [
      'activeDashboardFilters',
    ]),
    ...mapState('cards', [
      'allowCards',
    ]),
    ...mapGetters('dashboard', [
      'activeDashboard',
      'directAccess',
      'getCardByItemId',
    ]),
    ...mapGetters('user', [
      'isEditor',
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    shouldDisableChanges() {
      return !this.directAccess;
    },
    activeDashboardCard() {
      return merge([], this.activeDashboard.data.cards);
    },
    cards: {
      get() {
        return this.activeDashboardCard;
      },
      set(newCards) {
        this.saveNewCards(newCards);
      },
    },
    card() {
      return this.getCardByItemId(this.itemId);
    },
    xDateFormat() {
      const at = this.$t('mixins.cardsModel.atTime');
      const weekOf = this.$t('mixins.cardsModel.weekOf');
      const dateFormat = {
        [DATE_PORTION.BY_MINUTE]: `%d/%m/%Y ${at} %H:%M`,
        [DATE_PORTION.BY_HOUR]: `%d/%m/%Y ${at} %H:%M`,
        [DATE_PORTION.BY_DAY]: '%A, %d/%m/%Y',
        [DATE_PORTION.BY_WEEK]: `${weekOf} %d/%m/%Y`,
        [DATE_PORTION.BY_MONTH]: '%b, %Y',
      };

      return dateFormat[this.getDatePortion()];
    },
    labelFormat() {
      if (this.isQuarterOnDatePortion) {
        return;
      }

      const dateFormat = {
        [DATE_PORTION.BY_MINUTE]: '%H:%M',
        [DATE_PORTION.BY_HOUR]: '%H:%M',
        [DATE_PORTION.BY_DAY]: '%d/%m',
        [DATE_PORTION.BY_WEEK]: '%d/%m',
        [DATE_PORTION.BY_MONTH]: '%b',
      };

      return `{value:${dateFormat[this.getDatePortion()]}}`;
    },
    typeXAxios() {
      return this.isQuarterOnDatePortion ? 'category' : 'datetime';
    },
    isQuarterOnDatePortion() {
      return this.getDatePortion() === DATE_PORTION.BY_QUARTER;
    },
    hasFilterApplied() {
      return this.card?.props?.filter ? Object.keys(this.card.props.filter).length : false;
    },
    showNewCardMenuFeature() {
      return this.isEnabled(Flags.showNewCardMenu);
    },
    baseCardOptions() {
      const baseCardOptions = [
        {
          order: 1,
          name: this.$t('mixins.cardsModel.filter'),
          iconname: 'filter',
          function: DATA_TYPE_FILTERS_METHODS_OPTIONS[this.card.dataType],
          trackName: 'ST CardMenu Filter Click',
          hidden: this.card?.notSupportFilter,
          trackProperties: {
            title: this.card?.props?.title,
            widget: this.card?.component,
          },
        },
        {
          order: 2,
          name: this.$t('mixins.cardsModel.resize'),
          iconname: 'resize',
          function: 'openResizeModal',
          trackName: 'ST CardMenu Resize Click',
          trackProperties: {
            title: this.card?.props?.title,
            widget: this.card?.component,
          },
        },
        {
          order: 3,
          name: this.$t('mixins.cardsModel.expand'),
          iconname: 'arrow-expand-all',
          function: 'expandCardModal',
          trackName: 'ST CardMenu Expand Click',
          trackProperties: {
            title: this.card?.props?.title,
            widget: this.card?.component,
          },
        },
        {
          order: 4,
          name: this.$t('mixins.cardsModel.duplicate'),
          iconname: 'content-duplicate',
          function: 'duplicateCard',
          trackName: 'ST CardMenu Copy Click',
          trackProperties: {
            title: this.card?.props?.title,
            widget: this.card?.component,
          },
        },
        {
          order: 6,
          name: this.$t('mixins.cardsModel.remove'),
          iconname: 'delete',
          function: 'deleteCard',
          trackName: 'ST CardMenu Delete Click',
          isExpandedModeOption: true,
          trackProperties: {
            title: this.card?.props?.title,
            widget: this.card?.component,
          },
        },
      ];

      return this.showNewCardMenuFeature ? baseCardOptions : [];
    },
    cardOptionsWithCSVExportOption() {
      return this.baseCardOptions
        .concat(this.exportWithCSVOptionsData)
        .sort((actualOption, prevOption) => this.sortMenuOptions(actualOption, prevOption));
    },
    cardOptionsModals() {
      return [
        {
          componentName: 'CardModal',
          props: {
            componentName: this.card.props.title || '',
            value: this.showDeleteCardModal,
          },
          events: {
            confirm: this.deleteCard,
            input: (value) => {
              this.showDeleteCardModal = value;
            },
          },
        },
        {
          componentName: 'ResizeModal',
          props: {
            width: this.card.width || 0,
            height: this.card.height || 0,
            value: this.showResizeModal,
          },
          events: {
            'resize-width': this.resizeWidthCard,
            'resize-height': this.resizeHeightCard,
            input: (value) => {
              this.showResizeModal = value;
            },
          },
        },
        {
          componentName: 'filter/FilterListeningModal',
          props: {
            card: this.card,
            value: this.showFilterListeningModal,
          },
          events: {
            applyFilter: this.applyFilter,
            input: (value) => {
              this.showFilterListeningModal = value;
            },
          },
        },
        {
          componentName: 'filter/FilterSacModal',
          props: {
            card: this.card,
            value: this.showFilterSacModal,
          },
          events: {
            applyFilter: this.applyFilter,
            input: (value) => {
              this.showFilterSacModal = value;
            },
          },
        },
        {
          componentName: 'filter/FilterPageMetricsModal',
          props: {
            card: this.card,
            value: this.showFilterPageMetricsModal,
          },
          events: {
            applyFilter: this.applyFilter,
            input: (value) => {
              this.showFilterPageMetricsModal = value;
            },
          },
        },
        {
          componentName: 'filter/FilterComparativeModal',
          props: {
            card: this.card,
            value: this.showFilterCompetitiveModal,
          },
          events: {
            applyFilter: this.applyFilter,
            input: (value) => {
              this.showFilterCompetitiveModal = value;
            },
          },
        },
        {
          componentName: 'filter/FilterSacStatusModal',
          props: {
            card: this.card,
            value: this.showFilterSacStatusModal,
          },
          events: {
            applyFilter: this.applyFilter,
            input: (value) => {
              this.showFilterSacStatusModal = value;
            },
          },
        },
        {
          componentName: 'filter/FilterSacListeningModal',
          props: {
            card: this.card,
            value: this.showFilterSacListeningModal,
          },
          events: {
            applyFilter: this.applyFilter,
            input: (value) => {
              this.showFilterSacListeningModal = value;
            },
          },
        },
        {
          componentName: 'filter/FilterBlipModal',
          props: {
            card: this.card,
            value: this.showFilterBlipModal,
          },
          events: {
            applyFilter: this.applyFilter,
            input: (value) => {
              this.showFilterBlipModal = value;
            },
          },
        },
        {
          componentName: 'filter/GenericFilterModal',
          props: {
            card: this.card,
            value: this.showGenericFilterModal,
          },
          events: {
            applyFilter: this.applyFilter,
            input: (value) => {
              this.showGenericFilterModal = value;
            },
          },
        },
        {
          componentName: 'filter/TagViewModal',
          props: {
            card: this.card,
            value: this.showTagViewModal,
          },
          events: {
            applyFilter: this.applyFilter,
            input: (value) => {
              this.showTagViewModal = value;
            },
          },
        },
      ];
    },
  },
  methods: {
    ...mapActions('dashboard', [
      'saveNewCards',
    ]),
    saveCards() {
      return this.saveNewCards(this.activeDashboardCard);
    },
    getGridClassesByCard(card) {
      return [
        `col-xs-${Math.round(card.width * 4)}`,
        `col-sm-${Math.round(card.width * 2)}`,
        `col-md-${Math.round(card.width)}`,
        'export-card',
      ];
    },
    getDatePortion(options = {}) {
      const requestedDatePortion = options.granularity?.datePortion || this.card?.datePortion;
      return getUsage(requestedDatePortion).datePortion;
    },
    formattedCategoriesForQuarter(categories) {
      return this.isQuarterOnDatePortion
        ? categories.map(x => this.formattedLabelForQuarter(x))
        : categories;
    },
    formattedLabelForQuarter(label) {
      if (!label) {
        return;
      }

      const splittedLabel = label.toString().split('/');

      return `T${splittedLabel[1]} ${splittedLabel[0]}`;
    },
    expandCardModal() {
      this.showExpandCardModal = true;
    },
    openFilterListeningModal() {
      this.showFilterListeningModal = true;
    },
    openFilterSacModal() {
      this.showFilterSacModal = true;
    },
    openFilterPageMetricsModal() {
      this.showFilterPageMetricsModal = true;
    },
    openFilterCompetitiveModal() {
      this.showFilterCompetitiveModal = true;
    },
    openFilterSacStatusModal() {
      this.showFilterSacStatusModal = true;
    },
    openFilterSacListeningModal() {
      this.showFilterSacListeningModal = true;
    },
    openResizeModal() {
      this.showResizeModal = true;
    },
    openFilterBlipModal() {
      this.showFilterBlipModal = true;
    },
    openGenericFilterModal() {
      this.showGenericFilterModal = true;
    },
    openTagViewModal() {
      this.showTagViewModal = true;
    },
    async duplicateCard() {
      const duplicatedCard = cloneDeep(this.card);
      duplicatedCard.props.title = ` Cópia de ${duplicatedCard.props.title}`;
      duplicatedCard.id = generateRandomId();

      if (this.card.component === 'AnnotationImageCard' && this.card.props.hasUpload) {
        const obj = {
          oldId: this.card.id,
          newId: duplicatedCard.id,
          actualImage: this.card.props.image,
        };
        duplicatedCard.props.image = await this.duplicateImage(obj);
      }

      this.activeDashboard.data.cards.splice(this.indexToInsertDuplicateCard, 0, duplicatedCard);
      this.saveCards().then(() => {
        this.showExtraOptions = false;
      });
    },
    deleteCard() {
      this.isRemovingCard = true;

      this.activeDashboard.data.cards = this.activeDashboard.data.cards.filter((card) => {
        if (card.component === 'AnnotationImageCard' && card.props.hasUpload && card.id === this.itemId) {
          this.deleteImage(this.itemId);
        }
        return card.id !== this.itemId;
      });

      this.saveCards().then(() => {
        this.showExtraOptions = false;
        this.showDeleteCardModal = false;
        this.isRemovingCard = false;
      });
    },
    getKeyFromSizeType(SizeType, value) {
      return Object.keys(SizeType).find(key => SizeType[key] === value);
    },
    resizeTrackEvent(componentName, sizeType, sizeValue) {
      const data = {
        Widget: componentName,
        Title: this.card?.props?.title,
        SizeType: sizeType,
        SizeValue: sizeValue,
      };

      trackEventWithUserProperties('ST Card Options Resize', data);
    },
    resizeWidthCard(newWidth) {
      this.card.width = newWidth;

      const widthKey = this.getKeyFromSizeType(WIDTH, newWidth);
      this.resizeTrackEvent(this.card.props.title, 'Largura', WIDTH_SIZE_LABEL[widthKey]);

      this.card.id = generateRandomId();

      this.saveCards().then(() => {
        this.showExtraOptions = false;
      });
    },
    resizeHeightCard(newHeight) {
      this.card.height = newHeight;

      const heightKey = this.getKeyFromSizeType(HEIGHT, newHeight);
      this.resizeTrackEvent(this.card.props.title, 'Altura', HEIGHT_SIZE_LABEL[heightKey]);

      this.card.id = generateRandomId();

      this.saveCards().then(() => {
        this.showExtraOptions = false;
      });
    },
    hasSacTypeAll(value) {
      try {
        return value.hasOwnProperty(FILTER_NAMES.SAC_TYPE) ? Boolean(value.sac_type.value[0] === 'All') : false;
      } catch {
        return false;
      }
    },
    hasGlobalSacType() {
      return Boolean(this.globalFilter.sac_type);
    },
    filterTikTokPages(filters) {
      if (!filters?.pages && Boolean(this.card?.control?.tiktok.hideInChannel)) {
        const pagesList = [];
        const channelsList = new Set();
        this.applyControl = true;
        this.pagesFormatted.forEach(page => {
          pagesList.push(page.id);
          channelsList.add(page.channel);
        });
        return { ...filters, pages: { value: pagesList }, channels: { value: [...channelsList] } };
      }
      return { ...filters };
    },
    removeControlFilters() {
      if (this.applyControl) {
        delete this.card.props.filter.channel;
        delete this.card.props.filter.channels;
        delete this.card.props.filter.pages;
      }
    },
    // eslint-disable-next-line complexity
    async applyFilter(newFilters) {
      const checkHasSacTypeAll = this.hasSacTypeAll(newFilters);

      const sanatizedFilter = this.sanatizeCardFilter(newFilters);

      if (!sanatizedFilter) {
        delete this.card.props.filter;
        delete this.card.props.filterId;
        delete this.card.props.listeningFilter;
        delete this.card.props.listeningFilterId;
        delete this.card.props.sacFilter;
        delete this.card.props.sacFilterId;
      } else {
        if (checkHasSacTypeAll) {
          const checkHasGlobalSacType = this.hasGlobalSacType();
          sanatizedFilter.sac_type.value = checkHasGlobalSacType ? this.globalFilter.sac_type.value : sanatizedFilter.sac_type.value.filter(object => object !== 'All');
        }

        const oldFilters = { ...this.card.props.filter };

        this.card.props.filter = { ...this.filterTikTokPages(sanatizedFilter) };
        if (sanatizedFilter.listeningFilter && Object.keys(sanatizedFilter.listeningFilter).length === 0) {
          this.card.props.filter.listeningFilter = {
            channels: { value: channels.filter(channel => channel !== 'tiktok') },
          };
        }
        const changedFilters = Object.keys(this.differenceBetweenTwoObject(oldFilters, sanatizedFilter));
        const formatedFilters = this.applyFallbackToFilters(changedFilters, this.card.dataType);

        if (formatedFilters.length) {
          trackEventWithUserProperties('ST Card Filter Edit', { Field: formatedFilters });
        }
      }

      if (this.card.dataType === DATA_TYPE.SAC_LISTENING) {
        const newCardListeningFilter = await this.applyGlobalFilter({ card: this.card, filterType: DATA_TYPE.LISTENING });
        this.card.props.listeningFilterId = await WarRoomService.encodeFilter({
          account_id: this.accountId,
          is_staging: this.isStaging,
          filter: JSON.stringify(newCardListeningFilter),
        });
        const newCardSacFilter = await this.applyGlobalFilter({ card: this.card, filterType: DATA_TYPE.SAC });
        this.card.props.sacFilterId = await WarRoomService.encodeFilter({
          account_id: this.accountId,
          is_staging: this.isStaging,
          filter: JSON.stringify(newCardSacFilter),
        });
      } else {
        const newCardFilter = await this.applyGlobalFilter({ card: this.card });
        this.card.props.filterId = await WarRoomService.encodeFilter({
          account_id: this.accountId,
          is_staging: this.isStaging,
          filter: JSON.stringify(newCardFilter),
        });
      }

      if (checkHasSacTypeAll) {
        delete this.card.props.filter.sac_type;
      }
      this.removeControlFilters();

      this.card.id = generateRandomId();

      this.saveCards().then(() => {
        this.showExtraOptions = false;
      });
    },
    sortMenuOptions(actualOption, prevOption) {
      if (actualOption.order === prevOption.order) return 0;
      return actualOption.order < prevOption.order ? -1 : 1;
    },
    callOptionMenu(menuMethodName) {
      this.$attrs[menuMethodName]();
    },
    sanatizeCardFilter(filters) {
      if (Boolean(filters)) {
        const hasValue = (value) => {
          if (Array.isArray(value)) {
            return value.length > 0;
          }
          if (typeof value === 'boolean') {
            return true;
          }
          if (typeof value === 'string') {
            return value.trim() !== '';
          }
          return value !== null && value !== undefined;
        };

        if (this.card.dataType === DATA_TYPE.SAC_LISTENING) {
          return Object.keys(filters).reduce((sanitized, category) => {
            const sanitizedCategory = Object.keys(filters[category])
              .filter(filter => hasValue(filters[category][filter]?.value))
              .reduce((result, filter) => {
                result[filter] = filters[category][filter];
                return result;
              }, {});

            if (Object.keys(sanitizedCategory).length > 0) {
              sanitized[category] = sanitizedCategory;
            }

            return sanitized;
          }, {});
        }

        return Object.keys(filters)
          .filter(filterKey => {
            return hasValue(filters[filterKey]?.value);
          })
          .reduce((sanitized, filterKey) => {
            sanitized[filterKey] = filters[filterKey];

            return sanitized;
          }, {});
      }

      return undefined;
    },
  },
};
